import { Link } from "react-router-dom";
import { Gavel, FileText, MessageSquareText, Newspaper, ArrowRight, ExternalLink } from "lucide-react";
import { CATEGORIES, ARTICLES } from "@/data/resources";
import { PageHero, Breadcrumbs, FadeIn } from "@/components/Shared";

const ICONS = { Gavel, FileText, MessageSquareText, Newspaper };

export default function Resources() {
  const latest = ARTICLES.slice(0, 6);
  return (
    <main data-testid="resources-page">
      <PageHero
        eyebrow="Inform and empower"
        title="Legal Resources"
        lead="At Hallinans we aim to inform and empower you when it comes to understanding your legal rights. Our legal advice and information is here to help you understand the law, process and procedure across a wide variety of legal topics."
      />
      <Breadcrumbs items={[{ label: "Legal Resources" }]} />

      {/* Category cards */}
      <section className="bg-white py-20 sm:py-28 lg:py-32">
        <div className="brand-container">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {CATEGORIES.map((cat, i) => {
              const Icon = ICONS[cat.icon] || FileText;
              return (
                <FadeIn key={cat.slug} delay={i * 0.08} className="h-full">
                  <Link
                    to={`/resources/${cat.slug}`}
                    className="brand-card group flex h-full flex-col"
                    data-testid={`category-card-${cat.slug}`}
                  >
                    <div className="aspect-[16/9] overflow-hidden bg-brand-navy">
                      <img
                        src={cat.image}
                        alt={cat.title}
                        className="size-full object-cover opacity-80 transition-transform duration-300 group-hover:scale-[1.04]"
                      />
                    </div>
                    <div className="flex flex-1 flex-col p-6">
                      <Icon className="size-6 text-brand-gold" />
                      <h3 className="mt-4 text-lg font-medium text-brand-navy">{cat.title}</h3>
                      <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-500">
                        {cat.description}
                      </p>
                      <span className="brand-text-link mt-5 flex items-center gap-2">
                        Browse <ArrowRight className="size-3.5 transition-transform duration-200 group-hover:translate-x-1" />
                      </span>
                    </div>
                  </Link>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* Latest articles */}
      <section className="brand-panel-pale py-20 sm:py-28 lg:py-32" data-testid="latest-articles-section">
        <div className="brand-container">
          <FadeIn>
            <p className="brand-eyebrow">
              Latest
            </p>
            <h2 className="brand-heading mt-4 text-3xl sm:text-4xl">
              Recent insights
            </h2>
          </FadeIn>
          <div className="mt-10 divide-y divide-slate-200 border-y border-slate-200">
            {latest.map((a, i) => {
              const cat = CATEGORIES.find((c) => c.slug === a.category);
              const inner = (
                <div className="grid grid-cols-1 gap-2 py-6 transition-colors sm:grid-cols-12 sm:items-center sm:gap-6">
                  <p className="text-xs uppercase tracking-[0.18em] text-brand-gold sm:col-span-2">
                    {cat?.title}
                  </p>
                  <h3 className="text-lg font-medium text-brand-navy group-hover:text-brand-terracotta sm:col-span-7">
                    {a.title}
                    {a.externalUrl && <ExternalLink className="ml-2 inline size-3.5 text-slate-400" />}
                  </h3>
                  <p className="text-xs text-slate-500 sm:col-span-3 sm:text-right">
                    {a.author} · {a.date}
                  </p>
                </div>
              );
              return (
                <FadeIn key={a.slug} delay={i * 0.05}>
                  {a.body ? (
                    <Link to={`/resources/${a.category}/${a.slug}`} className="group block" data-testid={`latest-article-${a.slug}`}>
                      {inner}
                    </Link>
                  ) : (
                    <a href={a.externalUrl} target="_blank" rel="noopener noreferrer" className="group block" data-testid={`latest-article-${a.slug}`}>
                      {inner}
                    </a>
                  )}
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>
    </main>
  );
}
